import React, { useState } from 'react';

interface TargetPackageBuilderProps {
  entityId?: string;
  aoiId?: string;
  entityName?: string;
  open: boolean;
  onClose: () => void;
}

const getAuthToken = (): string | null =>
  localStorage.getItem('auth_token') || localStorage.getItem('token') || localStorage.getItem('access_token');

const SECTIONS = [
  { key: 'subject_summary', label: 'Subject Summary', desc: 'Name, type, aliases, modules' },
  { key: 'imagery_history', label: 'Imagery History', desc: 'Change detection records' },
  { key: 'spectral_analysis', label: 'Spectral Analysis', desc: 'NDVI/NDWI/NDBI trends' },
  { key: 'entity_associations', label: 'Entity Associations', desc: 'Relationship graph' },
  { key: 'intelligence_timeline', label: 'Intelligence Timeline', desc: 'Multi-module events' },
  { key: 'pol_assessment', label: 'Pattern of Life', desc: 'Baseline deviation analysis' },
  { key: 'risk_indicators', label: 'Risk Indicators', desc: 'Watchlist, sanctions, corroboration' },
  { key: 'raw_intelligence', label: 'Raw Intelligence', desc: 'Source observations' },
  { key: 'analyst_assessment', label: 'Analyst Assessment', desc: 'AI-generated + notes' },
  { key: 'tlp_classification', label: 'TLP Classification', desc: 'Handling markings' },
];

const OUTPUT_FORMATS = [
  { key: 'pdf', label: 'PDF Report', desc: 'Standard intelligence report' },
  { key: 'json', label: 'JSON Package', desc: 'Machine-readable data package' },
  { key: 'stix', label: 'STIX 2.1 Bundle', desc: 'For MISP/TAXII sharing' },
];

const TargetPackageBuilder: React.FC<TargetPackageBuilderProps> = ({
  entityId,
  aoiId,
  entityName,
  open,
  onClose,
}) => {
  const [selectedSections, setSelectedSections] = useState<string[]>(
    SECTIONS.map(s => s.key)
  );
  const [outputFormat, setOutputFormat] = useState('json');
  const [analystNotes, setAnalystNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const toggleSection = (key: string) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleAll = () => {
    if (selectedSections.length === SECTIONS.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(SECTIONS.map(s => s.key));
    }
  };

  const generate = async () => {
    setGenerating(true);
    setError(null);
    setResult(null);

    try {
      const token = getAuthToken();
      const body: any = {
        sections: selectedSections,
        output_format: outputFormat,
      };
      if (entityId) body.entity_id = entityId;
      if (aoiId) body.aoi_id = aoiId;
      if (analystNotes.trim()) body.analyst_notes = analystNotes.trim();

      const resp = await fetch('/api/geoint/wayback/target-package', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });

      if (resp.ok) {
        const data = await resp.json();
        setResult(data);
      } else {
        const text = await resp.text();
        setError(`Generation failed: ${resp.status} — ${text.slice(0, 200)}`);
      }
    } catch (err) {
      setError('Network error generating package');
    } finally {
      setGenerating(false);
    }
  };

  const downloadFile = (content: string, filename: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSTIX = async (packageId: string) => {
    try {
      const token = getAuthToken();
      const resp = await fetch(`/api/geoint/wayback/target-package/${packageId}/stix`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const data = await resp.json();
        downloadFile(JSON.stringify(data, null, 2), `stix_bundle_${packageId.slice(0, 8)}.json`, 'application/json');
      }
    } catch { /* ignore */ }
  };

  const downloadJSON = (data: any) => {
    downloadFile(JSON.stringify(data, null, 2), `target_package_${data.package_id?.slice(0, 8) || 'unknown'}.json`, 'application/json');
  };

  const downloadPDF = async (packageId: string) => {
    try {
      const token = getAuthToken();
      const resp = await fetch(`/api/geoint/wayback/target-package/${packageId}/pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (resp.ok) {
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `target_package_${packageId.slice(0, 8)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* ignore */ }
  };

  /** Render a section as structured intelligence output */
  const renderSection = (key: string, data: any): React.ReactNode => {
    if (!data) return <span className="text-muted-foreground">No data available</span>;
    if (data.error) return <span className="text-red-400">Error: {data.error}</span>;

    switch (key) {
      case 'subject_summary':
        return (
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div><span className="text-muted-foreground">Name:</span> <span className="text-foreground font-medium">{data.name || 'Unknown'}</span></div>
              <div><span className="text-muted-foreground">Type:</span> <span className="text-cyan-400">{data.entity_type || 'N/A'}</span></div>
              <div><span className="text-muted-foreground">Confidence:</span> <span className="text-foreground">{data.confidence != null ? `${(data.confidence * 100).toFixed(0)}%` : 'N/A'}</span></div>
              <div><span className="text-muted-foreground">Observations:</span> <span className="text-foreground">{data.observation_count || 0}</span></div>
              {data.first_seen && <div><span className="text-muted-foreground">First Seen:</span> <span className="text-foreground">{new Date(data.first_seen).toLocaleDateString()}</span></div>}
              {data.last_seen && <div><span className="text-muted-foreground">Last Seen:</span> <span className="text-foreground">{new Date(data.last_seen).toLocaleDateString()}</span></div>}
            </div>
            {data.last_location && (
              <div className="text-[11px]"><span className="text-muted-foreground">Location:</span> <span className="text-foreground">{data.last_location.lat?.toFixed(4)}, {data.last_location.lon?.toFixed(4)}</span></div>
            )}
            {data.contributing_modules?.length > 0 && (
              <div className="text-[11px]"><span className="text-muted-foreground">Modules:</span> {data.contributing_modules.map((m: string, i: number) => <span key={i} className="inline-block px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[10px] mr-1">{m}</span>)}</div>
            )}
            {data.resolution_keys && Object.keys(data.resolution_keys).length > 0 && (
              <div className="text-[11px]"><span className="text-muted-foreground">Resolution Keys:</span> {Object.entries(data.resolution_keys).map(([k, v]) => <span key={k} className="inline-block px-1.5 py-0.5 bg-muted rounded text-[10px] mr-1 font-mono">{k}: {String(v).slice(0, 20)}</span>)}</div>
            )}
          </div>
        );

      case 'imagery_history':
        if (!data.history?.length) return <span className="text-muted-foreground">No imagery history found</span>;
        return (
          <div className="space-y-1.5">
            <div className="text-[11px] text-muted-foreground">{data.total_detections} detection(s) found</div>
            {data.history.map((h: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-[11px] p-1.5 rounded bg-muted/30">
                <span className={`w-2 h-2 rounded-full shrink-0 ${h.status === 'completed' ? 'bg-green-500' : h.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                <span className="text-foreground font-medium">{h.change_percent?.toFixed(1) || '0'}% change</span>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">{h.method || 'unknown'}</span>
                {h.tiles_analyzed > 0 && <span className="text-muted-foreground">| {h.tiles_changed}/{h.tiles_analyzed} tiles</span>}
                {h.changed_area_km2 > 0 && <span className="text-muted-foreground">| {h.changed_area_km2.toFixed(3)} km²</span>}
                <span className="ml-auto text-muted-foreground text-[10px]">{h.date ? new Date(h.date).toLocaleDateString() : ''}</span>
              </div>
            ))}
            {data.history[0]?.change_types?.length > 0 && (
              <div className="mt-1.5">
                <div className="text-[10px] text-muted-foreground mb-1">Change Classification:</div>
                {data.history[0].change_types.map((ct: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="text-foreground capitalize">{ct.type}</span>
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500 rounded-full" style={{ width: `${Math.min(ct.percentage || 0, 100)}%` }} />
                    </div>
                    <span className="text-muted-foreground w-12 text-right">{ct.percentage?.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'spectral_analysis':
        return (
          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
              <div><span className="text-muted-foreground">Method:</span> <span className="text-foreground">{data.method || 'N/A'}</span></div>
              <div><span className="text-muted-foreground">Mean Magnitude:</span> <span className="text-foreground">{data.mean_change_magnitude || 0}</span></div>
              <div><span className="text-muted-foreground">Max Magnitude:</span> <span className="text-foreground">{data.max_change_magnitude || 0}</span></div>
            </div>
            {data.change_type_breakdown?.length > 0 ? (
              <div>
                <div className="text-[10px] text-muted-foreground mb-1">Change Categories:</div>
                {data.change_type_breakdown.map((ct: any, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-[11px] py-0.5">
                    <span className="w-20 text-foreground capitalize">{ct.type}</span>
                    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${ct.type === 'construction' ? 'bg-orange-500' : ct.type === 'vegetation' ? 'bg-green-500' : ct.type === 'water' ? 'bg-blue-500' : 'bg-purple-500'}`} style={{ width: `${Math.min(ct.percentage, 100)}%` }} />
                    </div>
                    <span className="text-muted-foreground w-12 text-right">{ct.percentage}%</span>
                    <span className="text-muted-foreground text-[10px] w-16 text-right">{ct.tile_count} tiles</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-[11px] text-muted-foreground">{data.note || 'No spectral change data'}</div>
            )}
            {data.indices_available?.length > 0 && (
              <div className="text-[11px]">
                <span className="text-muted-foreground">Indices:</span> {data.indices_available.map((idx: string, i: number) => <span key={i} className="inline-block px-1.5 py-0.5 bg-muted rounded text-[10px] mr-1">{idx}</span>)}
              </div>
            )}
          </div>
        );

      case 'entity_associations':
        if (!data.relationships?.length) return <span className="text-muted-foreground">No entity associations found</span>;
        return (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">{data.total} relationship(s)</div>
            {data.relationships.slice(0, 10).map((r: any, i: number) => (
              <div key={i} className="flex items-center gap-2 text-[11px] p-1 rounded bg-muted/30">
                <span className="text-cyan-400 font-mono text-[10px]">{r.source_entity_id?.slice(0, 8)}</span>
                <span className="text-yellow-400">{r.relationship_type}</span>
                <span className="text-cyan-400 font-mono text-[10px]">{r.target_entity_id?.slice(0, 8)}</span>
                <span className="ml-auto text-muted-foreground text-[10px]">{r.source_module} | {((r.confidence || 0) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        );

      case 'intelligence_timeline':
        if (!data.entries?.length) return <span className="text-muted-foreground">No timeline events</span>;
        return (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">{data.total} event(s)</div>
            {data.entries.map((e: any, i: number) => (
              <div key={i} className="flex items-start gap-2 text-[11px] p-1.5 rounded bg-muted/30">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-500 mt-1.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-foreground font-medium truncate">{e.name || 'Event'}</div>
                  <div className="text-muted-foreground text-[10px]">
                    {e.source_module} | {e.timestamp ? new Date(e.timestamp).toLocaleString() : 'Unknown time'}
                    {e.location && ` | ${e.location.lat?.toFixed(3)}, ${e.location.lon?.toFixed(3)}`}
                  </div>
                </div>
                <span className="text-muted-foreground text-[10px]">{((e.confidence || 0) * 100).toFixed(0)}%</span>
              </div>
            ))}
          </div>
        );

      case 'pol_assessment': {
        const deviation = data.deviation || {};
        const baseline = data.baseline || {};
        const severity = data.severity || data.raw_severity || 'UNKNOWN';
        const severityColor = severity === 'CRITICAL' ? 'text-red-400' : severity === 'HIGH' ? 'text-orange-400' : severity === 'MEDIUM' ? 'text-yellow-400' : 'text-green-400';
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-3 text-[11px]">
              <span className="text-muted-foreground">Severity:</span>
              <span className={`font-bold ${severityColor}`}>{severity}</span>
              {data.change_percent != null && (
                <><span className="text-muted-foreground">Change:</span> <span className="text-foreground">{data.change_percent}%</span></>
              )}
            </div>
            {deviation.score != null && (
              <div className="text-[11px]">
                <span className="text-muted-foreground">Deviation Score:</span> <span className="text-foreground">{deviation.score}</span>
                {deviation.assessment && <span className="text-muted-foreground ml-2">— {deviation.assessment}</span>}
              </div>
            )}
            {baseline.observation_count != null && (
              <div className="text-[11px]">
                <span className="text-muted-foreground">Baseline:</span> <span className="text-foreground">{baseline.observation_count} observations over {baseline.days || 90} days, avg change {baseline.avg_change_percent?.toFixed(1) || 0}%</span>
              </div>
            )}
            {data.correlation && (
              <div className="text-[11px]">
                <span className="text-muted-foreground">Nearby entities:</span> <span className="text-foreground">{data.correlation.entities_nearby || 0}</span>
                <span className="text-muted-foreground ml-2">Active modules:</span> <span className="text-foreground">{data.correlation.modules_active || 'none'}</span>
              </div>
            )}
            {data.note && <div className="text-[11px] text-muted-foreground italic">{data.note}</div>}
          </div>
        );
      }

      case 'risk_indicators':
        if (!data.indicators?.length) return <span className="text-muted-foreground">No risk indicators identified</span>;
        return (
          <div className="space-y-1">
            {data.indicators.map((ind: any, i: number) => (
              <div key={i} className={`flex items-center gap-2 text-[11px] p-1.5 rounded ${ind.severity === 'HIGH' ? 'bg-red-950/20 border border-red-800' : ind.severity === 'MEDIUM' ? 'bg-yellow-950/20 border border-yellow-800' : 'bg-muted/30 border border-border'}`}>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ind.severity === 'HIGH' ? 'bg-red-600 text-white' : ind.severity === 'MEDIUM' ? 'bg-yellow-600 text-black' : 'bg-muted text-foreground'}`}>{ind.severity}</span>
                <span className="text-foreground capitalize">{ind.type?.replace(/_/g, ' ')}</span>
                <span className="text-muted-foreground ml-auto">{ind.detail}</span>
              </div>
            ))}
          </div>
        );

      case 'raw_intelligence':
        if (!data.observations?.length) return <span className="text-muted-foreground">No raw observations</span>;
        return (
          <div className="space-y-1">
            <div className="text-[11px] text-muted-foreground">{data.total} observation(s)</div>
            {data.observations.slice(0, 8).map((o: any, i: number) => (
              <div key={i} className="text-[11px] p-1.5 rounded bg-muted/30 space-y-0.5">
                <div className="flex items-center gap-2">
                  <span className="text-foreground font-medium">{o.name || 'Observation'}</span>
                  <span className="ml-auto text-muted-foreground text-[10px]">{o.source_module}</span>
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {o.timestamp ? new Date(o.timestamp).toLocaleString() : ''} | Confidence: {((o.confidence || 0) * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        );

      case 'analyst_assessment':
        return (
          <div className="space-y-1.5">
            {data.ai_assessment && data.ai_assessment !== 'AI assessment unavailable' ? (
              <div className="text-[11px] text-foreground leading-relaxed whitespace-pre-wrap">{data.ai_assessment}</div>
            ) : (
              <div className="text-[11px] text-yellow-400">AI assessment not available — configure AI orchestrator for automated analysis</div>
            )}
            {data.analyst_notes && (
              <div className="text-[11px] mt-2 p-2 rounded bg-muted/30 border border-border">
                <div className="text-[10px] text-muted-foreground mb-1">Analyst Notes:</div>
                <div className="text-foreground">{data.analyst_notes}</div>
              </div>
            )}
          </div>
        );

      case 'tlp_classification': {
        const tlpColor = data.classification?.includes('RED') ? 'bg-red-600' : data.classification?.includes('AMBER') ? 'bg-amber-600' : data.classification?.includes('GREEN') ? 'bg-green-600' : 'bg-gray-600';
        return (
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[11px] font-bold text-white ${tlpColor}`}>{data.classification}</span>
            </div>
            <div className="text-[11px] text-muted-foreground">{data.reason}</div>
            <div className="text-[11px]"><span className="text-muted-foreground">Handling:</span> <span className="text-foreground">{data.handling_instructions}</span></div>
          </div>
        );
      }

      default:
        // Fallback for unknown sections
        return <pre className="text-[10px] text-muted-foreground whitespace-pre-wrap">{JSON.stringify(data, null, 2)}</pre>;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-foreground">Target Package Builder</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {entityName || entityId || aoiId || 'Unknown target'}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xl px-2">
            x
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!result ? (
            <>
              {/* Sections */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-foreground">Sections</h3>
                  <button
                    onClick={toggleAll}
                    className="text-[10px] text-cyan-400 hover:underline"
                  >
                    {selectedSections.length === SECTIONS.length ? 'Deselect all' : 'Select all'}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {SECTIONS.map(section => (
                    <button
                      key={section.key}
                      onClick={() => toggleSection(section.key)}
                      className={`text-left p-2 rounded border transition-colors ${
                        selectedSections.includes(section.key)
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-muted/30 text-muted-foreground'
                      }`}
                    >
                      <div className="text-xs font-medium">{section.label}</div>
                      <div className="text-[10px] opacity-70">{section.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Output format */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Output Format</h3>
                <div className="flex gap-2">
                  {OUTPUT_FORMATS.map(fmt => (
                    <button
                      key={fmt.key}
                      onClick={() => setOutputFormat(fmt.key)}
                      className={`flex-1 p-2 rounded border text-center transition-colors ${
                        outputFormat === fmt.key
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-border bg-muted/30 text-muted-foreground'
                      }`}
                    >
                      <div className="text-xs font-medium">{fmt.label}</div>
                      <div className="text-[10px] opacity-70">{fmt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Analyst notes */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">Analyst Notes</h3>
                <textarea
                  value={analystNotes}
                  onChange={(e) => setAnalystNotes(e.target.value)}
                  placeholder="Add assessment notes to include in the package..."
                  className="w-full h-20 px-3 py-2 bg-background border border-border rounded-md text-foreground text-xs placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                />
              </div>

              {error && (
                <div className="text-xs text-red-400 p-2 border border-red-600 rounded bg-red-950/20">
                  {error}
                </div>
              )}
            </>
          ) : (
            /* Results view */
            <div className="space-y-3">
              <div className="text-xs text-green-400 p-2 border border-green-600 rounded bg-green-950/20">
                Package generated successfully — {result.section_count} sections
              </div>

              <div className="text-xs space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Package ID</span>
                  <span className="text-foreground font-mono">{result.package_id?.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Generated</span>
                  <span className="text-foreground">{new Date(result.generated_at).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entity</span>
                  <span className="text-foreground">{result.entity_name} ({result.entity_type})</span>
                </div>
              </div>

              {/* Section previews */}
              <div className="space-y-2">
                {Object.entries(result.sections || {}).map(([key, data]: [string, any]) => {
                  const sectionMeta = SECTIONS.find(s => s.key === key);
                  const hasError = data?.error;
                  return (
                    <div key={key} className={`border rounded p-3 ${hasError ? 'border-red-800 bg-red-950/10' : 'border-border'}`}>
                      <div className="text-xs font-semibold text-foreground mb-1">
                        {sectionMeta?.label || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </div>
                      <div className="text-[11px] text-muted-foreground leading-relaxed">
                        {renderSection(key, data)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Download buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => downloadPDF(result.package_id)}
                  className="px-3 py-2 text-xs font-medium rounded bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                >
                  Download PDF
                </button>
                <button
                  onClick={() => downloadJSON(result)}
                  className="px-3 py-2 text-xs font-medium rounded bg-cyan-600 hover:bg-cyan-700 text-white transition-colors"
                >
                  Download JSON
                </button>
                <button
                  onClick={() => downloadSTIX(result.package_id)}
                  className="px-3 py-2 text-xs font-medium rounded bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                >
                  Download STIX
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!result && (
          <div className="p-4 border-t border-border shrink-0">
            <button
              onClick={generate}
              disabled={generating || selectedSections.length === 0}
              className="w-full px-4 py-2.5 text-sm font-medium rounded bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? 'Generating...' : `Generate Package (${selectedSections.length} sections)`}
            </button>
          </div>
        )}

        {result && (
          <div className="p-4 border-t border-border shrink-0">
            <button
              onClick={() => { setResult(null); setError(null); }}
              className="w-full px-4 py-2 text-sm font-medium rounded bg-muted text-foreground hover:bg-muted/80 transition-colors"
            >
              Generate Another
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TargetPackageBuilder;
